import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import USER_ROLE from 'constants/role';
import useTip from 'hooks/useTip.hook';
import { useCustomPermission } from 'providers/Access.provider';
import { useTranslation } from 'providers/GILT.provider';
import { selectHasClientContactFieldAccess } from 'selectors/clientPermissions.selectors';
import selectEffectiveUserRole from 'selectors/selectEffectiveUserRole';
import { selectHideCashup, selectHideDetails, selectHideStock } from 'selectors/user';
import fromAnyToString from 'utils/converters/fromAnyToString';
import logger from 'utils/logger.util';

const L = logger('TooltipForReceptionist');

const TooltipPermissions = ({ tooltipClass }) => {
  const componentRef = useRef(null);
  const { val: isOpen, yay: open, nay: close } = useTip(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (ev) => {
      const element = componentRef.current;
      if (element && element.contains(ev.target)) return;
      close();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [close]);

  return (
    <div
      className={cn({
        'tooltip-permissions': true,
        [tooltipClass]: tooltipClass,
      })}
      ref={componentRef}
      onClick={open}
      role="button"
      tabIndex="0"
    >
      {isOpen && (
        <div className="pink-area">
          <span className="tooltip-text" role="img" aria-labelledby={t('composite.tooltipForReceptionalist.managerAccess')}>
            🚫 {t('composite.tooltipForReceptionalist.managerAccess')} 🚫
          </span>
        </div>
      )}
    </div>
  );
};

const TooltipForReceptionist = ({ where = '', permission, grantAccess = false, checkCashup, checkStock, checkDetails, replacement = null }) => {
  const hideCashup = useSelector(selectHideCashup);
  const hideDetails = useSelector(selectHideDetails);
  const hideStock = useSelector(selectHideStock);
  const userRole = useSelector(selectEffectiveUserRole);
  const hasCustomPermission = useCustomPermission(permission);
  const hasClientContactFieldAccess = useSelector(selectHasClientContactFieldAccess);

  const effectiveGrantAccess = grantAccess || (permission && hasCustomPermission) || (checkDetails && hasClientContactFieldAccess);

  if (effectiveGrantAccess) return replacement;
  if (userRole !== USER_ROLE.receptionist) return replacement;
  if (checkCashup && hideCashup !== true) return replacement;
  if (checkStock && hideStock !== true) return replacement;
  if (checkDetails && hideDetails !== true) return replacement;

  const tooltipClass = fromAnyToString(where).trim();
  if (where && !tooltipClass) {
    L.warn('()', 'problem with class', where, 'converted to', tooltipClass);
  }

  return <TooltipPermissions tooltipClass={tooltipClass} />;
};

export default TooltipForReceptionist;
